import { getConfig } from './index';
import type { ApplicationConfig } from './ApplicationConfig';
import { Stage } from './ApplicationConfig';

export enum AdminApiDomain {
  ACTOR = 'actor',
  AGENT = 'agent',
  BRIDGE_TENANT = 'bridgeTenant',
  CAMPAIGN = 'campaign',
  COMPETITOR = 'competitor',
  COUPON = 'coupon',
  CRON_JOBS = 'cronJobs',
  DEVICE = 'device',
  FULFILLMENT = 'fulfillment',
  INGREDIENT = 'ingredient',
  GIGWORK = 'gigwork',
  N8N = 'n8n',
  ORDER = 'order',
  PRODUCT = 'product',
  QUESTIONNAIRE = 'questionnaire',
  RECOMMENDATION_LAB = 'recommendationLab',
  REVIEW = 'review',
  RULE_ENGINE = 'ruleEngine',
  SITE_CONFIG = 'siteconfig',
  SKIN_ANALYSIS_VALIDATION = 'skinAnalysisValidation',
  SKIN_ANALYSIS_DEMO = 'skinAnalysisDemo',
  SOURCING = 'sourcing',
  TAG = 'tag',
}

const prodReadyInternalApiDomains = new Set([
  AdminApiDomain.ACTOR,
  AdminApiDomain.AGENT,
  // The bridge tenant registry was born on internal-api; the facade never had these routes.
  AdminApiDomain.BRIDGE_TENANT,
  AdminApiDomain.CAMPAIGN,
  AdminApiDomain.COMPETITOR,
  AdminApiDomain.COUPON,
  AdminApiDomain.CRON_JOBS,
  AdminApiDomain.DEVICE,
  AdminApiDomain.FULFILLMENT,
  AdminApiDomain.INGREDIENT,
  AdminApiDomain.GIGWORK,
  AdminApiDomain.N8N,
  AdminApiDomain.ORDER,
  AdminApiDomain.PRODUCT,
  AdminApiDomain.REVIEW,
  AdminApiDomain.RULE_ENGINE,
  AdminApiDomain.SITE_CONFIG,
  AdminApiDomain.SKIN_ANALYSIS_VALIDATION,
  AdminApiDomain.SKIN_ANALYSIS_DEMO,
  AdminApiDomain.SOURCING,
  AdminApiDomain.TAG,
]);

const internalApiDomainsByStage: Record<Stage, ReadonlySet<AdminApiDomain>> = {
  [Stage.LOCAL]: prodReadyInternalApiDomains,
  [Stage.DEV]: prodReadyInternalApiDomains,
  [Stage.STAGING]: prodReadyInternalApiDomains,
  [Stage.PROD]: prodReadyInternalApiDomains,
};

export const shouldUseInternalApi = (stage: Stage, domain: AdminApiDomain): boolean => {
  return internalApiDomainsByStage[stage].has(domain);
};

export const resolveAdminApiUrl = (config: ApplicationConfig, domain: AdminApiDomain): string => {
  return shouldUseInternalApi(config.stage, domain) ? config.internalApiUrl : config.apiUrl;
};

export const getAdminApiUrl = (domain: AdminApiDomain): string => {
  return resolveAdminApiUrl(getConfig(), domain);
};

/**
 * Base URL a merchant pastes into the Shopify theme editor so the bridge extension can post events.
 *
 * Public facade rather than internal-api: this one is called by shoppers' browsers, not by staff.
 * The extension appends `/bridgeEvents`, so the trailing slash is stripped here — with it, the
 * request becomes `//bridgeEvents`, 404s, and the extension discards the failure silently.
 */
export const resolveBridgeCollectionUrl = (config: ApplicationConfig): string => {
  return `${config.apiUrl.replace(/\/+$/, '')}/business`;
};

export const getBridgeCollectionUrl = (): string => {
  return resolveBridgeCollectionUrl(getConfig());
};
